import { useMemo, useState } from "react";
import { ArrowDownTrayIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, FileUpload, InfoBox, Modal, Table, Tag } from "../ui";
import { errorMessageOf } from "../../api/scholarships";
import { parseExcelFile } from "../SpreadsheetUpload";
import { downloadSheets } from "../../utils/excel";
import { countries } from "../data/countries";
import { useEducationLevels } from "./model";

const COLUMNS = {
  name: "Programme name",
  institutionName: "University or institution",
  country: "Country",
  level: "Education level",
  durationMonths: "Duration (months)",
};

const key = (text) => String(text ?? "").trim().toLowerCase();
const COUNTRY_CODES = Object.fromEntries(countries.flatMap((c) => [[key(c.code), c.code], [key(c.name), c.code]]));

// A cell by its column header, ignoring case and spaces around it
const cellOf = (row, header) => {
  const found = Object.keys(row).find((k) => key(k) === key(header));
  return found === undefined ? "" : String(row[found]).trim();
};

/**
 * One spreadsheet row as the API takes it, with what is wrong with it. `place` says whether the
 * university and country are "required", "optional" or not part of a programme ("none").
 */
const rowOf = (row, levels, place) => {
  const errors = [];
  const name = cellOf(row, COLUMNS.name);
  const institutionName = cellOf(row, COLUMNS.institutionName);
  const country = cellOf(row, COLUMNS.country);
  const levelName = cellOf(row, COLUMNS.level);
  const duration = cellOf(row, COLUMNS.durationMonths);

  if (!name) errors.push("Programme name is missing");
  else if (name.length > 200) errors.push("Programme name is longer than 200 characters");
  const level = levels.find((l) => key(l.levelName) === key(levelName));
  if (!levelName) errors.push("Education level is missing");
  else if (!level) errors.push(`Unknown education level "${levelName}"`);
  const months = Number(duration);
  if (!duration) errors.push("Duration is missing");
  else if (!Number.isInteger(months) || months < 1 || months > 120) errors.push("Duration must be whole months from 1 to 120");

  const countryCode = COUNTRY_CODES[key(country)];
  if (place !== "none") {
    if (place === "required" && !institutionName) errors.push("University or institution is missing");
    if (institutionName.length > 200) errors.push("University or institution is longer than 200 characters");
    if (place === "required" && !country) errors.push("Country is missing");
    if (country && !countryCode) errors.push(`Unknown country "${country}"`);
  }

  const body = {
    name,
    ...(place !== "none" && { institutionName: institutionName || null, countryCode: countryCode || null }),
    levelId: level?.id,
    durationMonths: months,
  };
  return { line: row.__rowNum__ + 1, body, levelName: level?.levelName ?? levelName, country: countryCode ?? country, errors };
};

// The same programme twice in one file is imported once
const sameKey = (r) => [r.body.name, r.body.institutionName, r.body.countryCode, r.body.levelId].map(key).join("|");
const withRepeats = (rows) => {
  const seen = new Map();
  return rows.map((r) => {
    if (r.errors.length) return r;
    const first = seen.get(sameKey(r));
    if (first) return { ...r, errors: [`Same programme as row ${first}`] };
    seen.set(sameKey(r), r.line);
    return r;
  });
};

const STATUS = {
  added: <Tag color="green">Added</Tag>,
  existing: <Tag color="blue">Already in the catalogue</Tag>,
  failed: <Tag color="red">Failed</Tag>,
};

/**
 * Imports programmes from an Excel sheet (one per row, with the template's headers) instead of
 * entering them one by one. Every row is checked before anything is sent; the valid ones are then
 * saved one at a time through `saveRow(body)`, which answers `{ id, existing }`. `onImported` gets
 * the IDs of the programmes saved or found in the catalogue. At most `limit` rows.
 */
export default function ProgrammeImportDialog({ open, place = "required", limit = 500, saveRow, onImported, onClose }) {
  const { data: levels = [], isError: levelsFailed } = useEducationLevels();
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null); // the sheet's rows, as read
  const [error, setError] = useState("");
  const [results, setResults] = useState({}); // spreadsheet row → { status, message }
  const [progress, setProgress] = useState(null);

  const rows = useMemo(() => (parsed ? withRepeats(parsed.map((r) => rowOf(r, levels, place))) : []), [parsed, levels, place]);
  // Rows not imported yet, failed ones included so they can be tried again
  const ready = rows.filter((r) => !r.errors.length && (!results[r.line] || results[r.line].status === "failed"));
  const importing = progress !== null;
  const done = Object.keys(results).length > 0;

  const reset = () => {
    setFile(null);
    setParsed(null);
    setError("");
    setResults({});
  };

  const close = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const pick = async (picked) => {
    reset();
    setFile(picked);
    if (!picked) return;
    try {
      const sheet = await parseExcelFile(picked);
      if (!sheet.length) return setError("The sheet has no rows. Fill in the template, one programme per row.");
      if (sheet.length > limit) return setError(`The sheet has ${sheet.length} rows; import at most ${limit} at a time.`);
      setParsed(sheet);
    } catch (err) {
      console.error("Failed to read the programmes sheet:", err);
      setError("Could not read the file. Use the template and save it as .xlsx.");
    }
  };

  const downloadTemplate = () => {
    const headers = Object.entries(COLUMNS)
      .filter(([field]) => place !== "none" || !["institutionName", "country"].includes(field))
      .map(([, header]) => header);
    downloadSheets(
      [
        { name: "Programmes", rows: [headers] },
        { name: "Education levels", rows: [["Education level"], ...levels.map((l) => [l.levelName])] },
      ],
      "programmes-template"
    );
  };

  const runImport = async () => {
    const outcome = { ...results };
    const ids = [];
    setProgress({ current: 0, total: ready.length });
    for (const [i, row] of ready.entries()) {
      try {
        const saved = await saveRow(row.body);
        if (saved.id) ids.push(saved.id);
        outcome[row.line] = { status: saved.existing ? "existing" : "added" };
      } catch (err) {
        console.error(`Failed to import programme on row ${row.line}:`, err);
        outcome[row.line] = { status: "failed", message: errorMessageOf(err, "Could not save this programme.") };
      }
      setResults({ ...outcome });
      setProgress({ current: i + 1, total: ready.length });
    }
    const failed = ready.filter((r) => outcome[r.line].status === "failed").length;
    const saved = ready.length - failed;
    try {
      if (ids.length) await onImported(ids);
      if (failed) toast.error(`${failed} programme${failed === 1 ? "" : "s"} could not be imported; see the list.`);
      else toast.success(`${saved} programme${saved === 1 ? "" : "s"} imported`);
    } catch (err) {
      console.error("Failed to add the imported programmes:", err);
      toast.error(errorMessageOf(err, "The programmes were saved but could not be added. Please try again."));
    } finally {
      setProgress(null);
    }
  };

  const invalid = rows.filter((r) => r.errors.length).length;
  const columns = [
    { key: "line", label: "Row", accessor: (r) => r.line },
    { key: "name", label: "Programme", render: (r) => <span className="font-medium text-slate-800">{r.body.name || "—"}</span> },
    ...(place !== "none"
      ? [
          { key: "institutionName", label: "University", accessor: (r) => r.body.institutionName || "—" },
          { key: "country", label: "Country", accessor: (r) => r.country || "—" },
        ]
      : []),
    { key: "level", label: "Level", accessor: (r) => r.levelName || "—" },
    { key: "duration", label: "Months", accessor: (r) => (Number.isFinite(r.body.durationMonths) ? r.body.durationMonths : "—") },
    {
      key: "status",
      label: "Status",
      render: (r) => {
        const result = results[r.line];
        if (r.errors.length) return <span className="text-xs text-red-700">{r.errors.join("; ")}</span>;
        if (!result) return <Tag color="gray">Ready</Tag>;
        return (
          <div className="flex flex-col gap-1">
            {STATUS[result.status]}
            {result.message && <span className="text-xs text-red-700">{result.message}</span>}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onClose={close}
      dismissible={!importing}
      size="xl"
      title="Import programmes from Excel"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={importing}>
            {done ? "Close" : "Cancel"}
          </Button>
          <Button icon={<ArrowUpTrayIcon className="h-4 w-4" />} onClick={runImport} loading={importing} disabled={!ready.length || levelsFailed}>
            {importing ? `Importing ${progress.current} of ${progress.total}` : `Import ${ready.length} programme${ready.length === 1 ? "" : "s"}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InfoBox>
          Fill in the template, one programme per row. Use the education levels listed in its second sheet
          {place !== "none" && ", and a country's name or its two-letter code (e.g. CN)"}
          {place === "optional" && ". The university and country may be left empty"}. A programme already in the catalogue is reused.
        </InfoBox>
        <Button variant="outline" icon={<ArrowDownTrayIcon className="h-4 w-4" />} onClick={downloadTemplate} disabled={!levels.length} className="w-fit">
          Download template
        </Button>
        <FileUpload
          label="Programmes sheet"
          required
          accept=".xlsx,.xls,.csv"
          hint={`Excel or CSV, at most ${limit} rows`}
          file={file}
          onChange={pick}
          error={error || (levelsFailed ? "Could not load the education levels. Please try again later." : "")}
        />
        {rows.length > 0 && (
          <>
            <p className="text-sm text-slate-600">
              {rows.length} row{rows.length === 1 ? "" : "s"} read
              {invalid > 0 && `; ${invalid} with problems will be skipped. Fix them in the sheet and choose it again to include them`}.
            </p>
            <Table columns={columns} data={rows} rowKey={(r) => r.line} searchable={false} exportable={false} />
          </>
        )}
      </div>
    </Modal>
  );
}
